/**
 * src/components/Financial/PlanetInfoTransit.jsx
 * 【完全版：動的Orb設定連動 + デザイン復元 + 構文エラー修正済】
 */
import React, { useState, useMemo, useCallback } from "react";
import {
  ChevronDown,
  ChevronUp,
  Globe,
  Zap,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";
import * as Astro from "../../astronomy.js";
import { PLANET_SYMBOLS } from "../../utils/constants.js";
import { styles as commonStyles } from "../../utils/styles.js";

// 金融占星術的解釈データ
const FINANCIAL_DATA = {
  planets: {
    Sun: ["中心価格", "市場の活力", "公的トレンド"],
    Moon: ["大衆心理", "センチメント", "短期的な変動"],
    Mercury: ["取引高", "ニュース・噂", "短期トレンド"],
    Venus: ["資産価値", "強気心理", "通貨・為替"],
    Mars: ["エネルギー価格", "急変・衝突", "攻撃的売買"],
    Jupiter: ["強気市場", "上昇拡大", "金融緩和"],
    Saturn: ["弱気市場", "下落抑制", "債務・規制"],
    Uranus: ["ボラティリティ", "技術革新", "突発的ニュース"],
    Neptune: ["原油・液体", "不透明感", "投機的バブル"],
    Pluto: ["構造転換", "巨額資金", "市場の破壊と再生"],
    Earth: ["実体経済", "供給網", "物理的需要"],
    Chiron: ["市場の傷", "回復プロセス", "心理的トラウマ"],
  },
  signs: {
    Aries:
      "積極的な投資意欲。エネルギー・軍需セクターへの注目。短期的な決断が促されます。",
    Taurus:
      "安定志向。実物資産や食品・金融セクターの堅調さ。長期的な価値の保存が重視されます。",
    Gemini:
      "情報の錯綜による乱高下。通信・IT・物流の活発化。短期ニュースに敏感な相場環境。",
    Cancer:
      "住宅関連、食料、防衛、内需関連銘柄への資金移動。感情に左右されやすい局面。",
    Leo: "投機への関心。娯楽・宝飾・投機への関心。強気なトレンド形成と過信に注意が必要な時期。",
    Virgo:
      "細かな分析と調整。ヘルスケア・精密機械の精査。効率性と実利が優先される動き。",
    Libra:
      "均衡を保つ動き。外交・法規制の影響を受けやすく、パートナーシップや合意が鍵となります。",
    Scorpio:
      "深刻な債務問題や秘密裏の資金移動。資源・保険。市場の深部にある変容が表面化します。",
    Sagittarius:
      "海外投資の活発化。空運・貿易・教育セクター。楽観論による市場の拡大が期待されます。",
    Capricorn:
      "慎重な長期投資。建設・公共インフラ・伝統的企業。組織力と規律が市場を支配します。",
    Aquarius:
      "ハイテク・IT・暗号資産への資金集中。非伝統的な市場アプローチや技術革新が相場を牽引します。",
    Pisces:
      "市場の迷走。石油・製薬・化学。感傷的・直感的な要因で価格が動きやすい不透明な時期。",
  },
};

const QUALITY_STYLE = {
  Cardinal: { bg: "#8b5cf6", label: "CARDINAL(活動宮)" },
  Fixed: { bg: "#c77a0a", label: "FIXED(固定宮)" },
  Mutable: { bg: "#27ae60", label: "MUTABLE(柔軟宮)" },
};

const ELEMENT_STYLE = {
  Fire: { bg: "#e74c3c", label: "FIRE(火)" },
  Earth: { bg: "#27ae60", label: "EARTH(土)" },
  Air: { bg: "#f1c40f", label: "AIR(風)" },
  Water: { bg: "#3498db", label: "WATER(水)" },
};

const CRITICAL_TYPE_NAMES = {
  Cardinal: "カーディナル",
  Fixed: "固定",
  Mutable: "ミュータブル",
  Anaretic: "アナレティック",
  Avatar: "アバター",
  Ingress: "イングレス",
};

const CRITICAL_TYPE_COLORS = {
  Cardinal: "#e74c3c",
  Fixed: "#c77a0a",
  Mutable: "#9b59b6",
  Anaretic: "#c0392b",
  Avatar: "#1a7a3c",
  Ingress: "#3498db",
};

const HARD_ASPECTS = [
  "Conjunction",
  "Opposition",
  "Square",
  "Semi-square",
  "Sesquiquadrate",
  "Quincunx",
];
const OUTER_PLANETS_LIST = ["Jupiter", "Saturn", "Uranus", "Neptune", "Pluto"];

// SquareのWAX/WAN判定用ヘルパー関数
const getSquareLabel = (p1, lon1, p2, lon2) => {
  if (lon1 === undefined || lon2 === undefined) return "";

  const order = [
    "Moon",
    "Mercury",
    "Venus",
    "Sun",
    "Mars",
    "Jupiter",
    "Saturn",
    "Uranus",
    "Neptune",
    "Pluto",
  ];
  const idx1 = order.indexOf(p1);
  const idx2 = order.indexOf(p2);

  let fastLon = lon1,
    slowLon = lon2;
  // インデックスが小さい方が足が速い（Moon=0, Pluto=9）と定義
  if (idx1 !== -1 && idx2 !== -1 && idx1 > idx2) {
    fastLon = lon2;
    slowLon = lon1;
  }

  const phase = (fastLon - slowLon + 360) % 360;
  return phase < 180 ? "(WAX)" : "(WAN)";
};

// 補助関数：度°分′形式
function formatDegree(deg) {
  var degrees = Math.floor(deg);
  var minutes = Math.round((deg - degrees) * 60);
  return minutes > 0 ? degrees + "°" + minutes + "′" : degrees + "°";
}

export default function PlanetInfoTransit(props) {
  var transitPositions = props.transitPositions;
  var transitDate = props.transitDate;
  var isHeliocentric = props.isHeliocentric;
  var showExtendedPoints = props.showExtendedPoints;
  var isRetro = props.isRetro;
  var planetColors = props.planetColors;
  var signColors = props.signColors;
  var isDarkMode = props.isDarkMode;
  var isMobile = props.isMobile;
  // 設定データの受け取り：オブジェクトが存在しても中身が空の場合に備え、プロパティ単位でデフォルト値を適用
  var settings = props.aspectSettings || {};
  var aspectSettings = {
    majorOrb: settings.majorOrb !== undefined ? settings.majorOrb : 8.0,
    minorOrb: settings.minorOrb !== undefined ? settings.minorOrb : 2.0,
    outerPlanetOrb:
      settings.outerPlanetOrb !== undefined ? settings.outerPlanetOrb : 1.0,
    useOuterLimit:
      settings.useOuterLimit !== undefined ? settings.useOuterLimit : false,
  };

  const [openId, setOpenId] = useState(null);

  // 全クリティカル警告を事前に計算 (Orb 0.5固定)
  const criticalAlerts = useMemo(
    function () {
      if (!transitPositions) return [];
      var filteredPositions = Object.assign({}, transitPositions);
      if (!isHeliocentric) delete filteredPositions["Earth"];
      return Astro.getAllCriticalDegrees(filteredPositions, 0.5);
    },
    [transitPositions, isHeliocentric],
  );

  /**
   * 表示する天体リストの定義（NorthNode/SouthNodeを追加）
   */
  const displayPlanets = useMemo(
    function () {
      var base = isHeliocentric
        ? [
          "Earth",
          "Mercury",
          "Venus",
          "Mars",
          "Jupiter",
          "Saturn",
          "Uranus",
          "Neptune",
          "Pluto",
        ]
        : [
          "Sun",
          "Moon",
          "Mercury",
          "Venus",
          "Mars",
          "Jupiter",
          "Saturn",
          "Uranus",
          "Neptune",
          "Pluto",
          "NorthNode", // ノースノード
          "SouthNode", // サウスノード
        ];

      if (showExtendedPoints) {
        // 拡張表示モード時はノードを重複させないように制御
        var filteredBase = base.filter(function (p) {
          return p !== "NorthNode" && p !== "SouthNode";
        });
        return filteredBase.concat(["NorthNode", "SouthNode"]);
      }
      return base;
    },
    [isHeliocentric, showExtendedPoints],
  );

  const formatDMS = function (lon) {
    var deg = Math.floor(lon % 30);
    var min = Math.floor(((lon % 30) - deg) * 60);
    var sec = Math.floor((((lon % 30) - deg) * 60 - min) * 60);
    return (
      deg +
      "°" +
      min.toString().padStart(2, "0") +
      "'" +
      sec.toString().padStart(2, "0") +
      '"'
    );
  };

  const getPlanetSpeedAt = useCallback(
    function (planetId, date) {
      var p1 = Astro.getPlanetLongitude(planetId, date, isHeliocentric);
      var p2 = Astro.getPlanetLongitude(
        planetId,
        new Date(date.getTime() + 86400000),
        isHeliocentric,
      );
      var diff = p2 - p1;
      if (diff > 180) diff -= 360;
      if (diff < -180) diff += 360;
      return diff;
    },
    [isHeliocentric],
  );

  const calculateNextRetrograde = useCallback(
    function (planetId, startDate) {
      if (
        isHeliocentric ||
        planetId === "Sun" ||
        planetId === "Moon" ||
        planetId.indexOf("Node") !== -1
      )
        return null;
      var checkDate = new Date(startDate.getTime());
      var prevSpeed = getPlanetSpeedAt(planetId, checkDate);
      for (var i = 1; i < 500; i++) {
        var nextDate = new Date(startDate.getTime() + i * 86400000);
        var currentSpeed = getPlanetSpeedAt(planetId, nextDate);
        if (prevSpeed > 0 && currentSpeed < 0) {
          var low = nextDate.getTime() - 86400000;
          var high = nextDate.getTime();
          for (var j = 0; j < 10; j++) {
            var mid = (low + high) / 2;
            if (getPlanetSpeedAt(planetId, new Date(mid)) < 0) high = mid;
            else low = mid;
          }
          return new Date(high);
        }
        prevSpeed = currentSpeed;
      }
      return null;
    },
    [isHeliocentric, getPlanetSpeedAt],
  );

  const getTransitAspects = function (targetId) {
    var results = [];
    var p1Lon = transitPositions[targetId];
    if (p1Lon == null || (!isHeliocentric && targetId === "Earth")) return [];

    Object.keys(transitPositions).forEach(function (p2) {
      if (p2 === targetId) return;
      if (!isHeliocentric && p2 === "Earth") return;
      
      // ハウス尖点 (H1-H12) およびアングル (ASC, DSC, MC, IC) はアスペクト表示から除外
      if (/^H\d+$/.test(p2)) return;
      if (["ASC", "DSC", "MC", "IC", "SouthNode"].includes(p2)) return;

      var p2Lon = transitPositions[p2];
      if (p2Lon == null) return;

      var diff = Math.abs(p1Lon - p2Lon) % 360;
      var dist = diff > 180 ? 360 - diff : diff;

      // Astro.ASPECTS 自体の存在チェック
      if (!Astro.ASPECTS || !Astro.ASPECTS.major) return;
      var allAspects = [].concat(
        Astro.ASPECTS.major,
        Astro.ASPECTS.minor || [],
      );

      allAspects.forEach(function (asp) {
        var orbValue = Math.abs(dist - asp.angle);

        // 主要アスペクトかどうかの判定
        var isMaj = false;
        for (var i = 0; i < Astro.ASPECTS.major.length; i++) {
          if (Astro.ASPECTS.major[i].name === asp.name) {
            isMaj = true;
            break;
          }
        }

        // 基本の制限値設定
        var currentLimit =
          isMaj === true ? aspectSettings.majorOrb : aspectSettings.minorOrb;

        // 外惑星同士(木星以降)の特殊制限チェック
        var isBothOuter =
          OUTER_PLANETS_LIST.indexOf(targetId) !== -1 &&
          OUTER_PLANETS_LIST.indexOf(p2) !== -1;
        if (aspectSettings.useOuterLimit === true && isBothOuter === true) {
          currentLimit = aspectSettings.outerPlanetOrb;
        }

        // 数値として有効な currentLimit がある場合のみ比較
        if (typeof currentLimit === "number" && orbValue <= currentLimit) {
          var type = HARD_ASPECTS.indexOf(asp.name) !== -1 ? "Hard" : "Soft";
          results.push({
            name: asp.name,
            symbol: asp.symbol,
            angle: asp.angle,
            p2: p2,
            orb: orbValue,
            dist: dist,
            sign2: Astro.getZodiacSign(p2Lon),
            type: type,
            limit: currentLimit, // 判定に使用した制限値を保持しておく
          });
        }
      });
    });
    return results.sort(function (a, b) {
      return a.orb - b.orb;
    });
  };

  var isDarkMode = props.isDarkMode;

  return (
    <div style={{ width: "100%" }}>
      <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <div
          className="preserved-grid"
          style={{
            minWidth: isMobile ? "700px" : "1000px",
            display: "flex",
            flexDirection: "column",
            gap: "1px",
            background: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "#f1f5f9",
          }}
        >
          {/* ヘッダー：ハウス列(0.7fr)を追加し、比率を調整 */}
          <div
            className="preserved-grid"
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "0.9fr 1.4fr 0.8fr 0.5fr 1fr 0.9fr 30px"
                : "1.1fr 1.8fr 1fr 0.7fr 1.2fr 1.1fr 30px",
              padding: isMobile ? "12px 6px" : "12px 16px",
              background: isDarkMode
                ? "rgba(255, 255, 255, 0.08)"
                : "#f8fafc",
              fontSize: "11px",
              fontWeight: "bold",
              color: isDarkMode ? "#ffffff" : "#64748b",
            }}
          >
            <div>天体</div>
            <div>サイン</div>
            <div>赤緯</div>
            <div style={{ textAlign: "center" }}>ハウス</div>
            <div style={{ textAlign: "center" }}>クオリティ</div>
            <div style={{ textAlign: "center" }}>エレメント</div>
            <div></div>
          </div>

          {displayPlanets.map(function (id) {
            // 1. NorthNode/SouthNode の実効IDマッピング
            var effectiveId = id;

            var lon = transitPositions[effectiveId];
            // SouthNodeの座標が直接ない場合はNorthNodeの反対側を計算
            if (
              id === "SouthNode" &&
              lon === undefined &&
              transitPositions.NorthNode !== undefined
            ) {
              lon = (transitPositions.NorthNode + 180) % 360;
            }

            if (lon == null) return null;

            var sign = Astro.getZodiacSign(lon);
            var decl = Astro.getDeclination(effectiveId, transitDate);

            // --- 2. [修正版] ハウス判定：変数のスコープ修正とプラシーダス対応 ---
            var houseNum = "---";
            var asc = transitPositions.ASC;
            var cusps = [];

            if (props.houseSystem === "equal") {
              // 等分ハウス：ASCを起点に30度刻みで判定
              if (asc !== undefined && asc !== null) {
                houseNum = Math.floor(((lon - asc + 360) % 360) / 30) + 1;
              }
            } else {
              // プラシーダス：astronomy.js で計算された H1~H12 の尖点を取得
              for (var h = 1; h <= 12; h++) {
                cusps.push(transitPositions["H" + h]);
              }

              // すべての尖点(H1~H12)が数値として存在する場合のみ判定を実行
              if (
                cusps.length === 12 &&
                cusps.every(function (c) {
                  return typeof c === "number";
                })
              ) {
                for (var i = 0; i < 12; i++) {
                  var start = cusps[i];
                  var end = i === 11 ? cusps[0] : cusps[i + 1];

                  var isInHouse = false;
                  if (start < end) {
                    if (lon >= start && lon < end) isInHouse = true;
                  } else {
                    // 360度（サインの境界）を跨ぐ場合
                    if (lon >= start || lon < end) isInHouse = true;
                  }

                  if (isInHouse === true) {
                    houseNum = i + 1;
                    break;
                  }
                }
              } else if (asc !== undefined && asc !== null) {
                // フォールバック：尖点データがなければ等分計算
                houseNum = Math.floor(((lon - asc + 360) % 360) / 30) + 1;
              }
            }

            // デバッグログ：ASCが Astro-Seek(Gemini 9°) と一致するか確認
            if (id === "Sun") {
              console.log("--- [DEBUG: Table] ASC/MC Check ---", {
                ASC: asc,
                MC: transitPositions.MC,
                House: houseNum,
              });
            }

            var dignity = Astro.getDignity(effectiveId, sign.name);

            var isOpen = openId === id;
            var keywordsList = FINANCIAL_DATA.planets[id] ||
              FINANCIAL_DATA.planets[effectiveId] || ["-", "-", "-"];
            var signInterp =
              FINANCIAL_DATA.signs[sign.name] || "解釈データなし";
            var planetAspects = getTransitAspects(effectiveId);
            var nextRetro = calculateNextRetrograde(effectiveId, transitDate);
            var planetCrit = criticalAlerts.find(function (a) {
              return a.planet === effectiveId;
            });

            var declAbs = Math.abs(decl || 0);
            var declDisplay =
              (effectiveId === "Earth" && isHeliocentric) || decl === null
                ? "---"
                : (decl < 0 ? "-" : "") +
                Math.floor(declAbs) +
                "°" +
                Math.floor((declAbs % 1) * 60)
                  .toString()
                  .padStart(2, "0") +
                "'" +
                Math.floor((((declAbs % 1) * 60) % 1) * 60)
                  .toString()
                  .padStart(2, "0") +
                '"';

            return (
              <div
                key={id}
                style={{ background: isDarkMode ? "transparent" : "#fff" }}
              >
                <div
                  onClick={function () {
                    setOpenId(isOpen ? null : id);
                  }}
                  className="preserved-grid"
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "0.9fr 1.4fr 0.8fr 0.5fr 1fr 0.9fr 30px"
                      : "1.1fr 1.8fr 1fr 0.7fr 1.2fr 1.1fr 30px",
                    padding: isMobile ? "12px 6px" : "12px 16px",
                    alignItems: "center",
                    cursor: "pointer",
                    borderBottom: "1px solid #f0f0f075",
                    color: isDarkMode ? "#ffffff" : "inherit",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "28px 1fr",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "24px",
                        color: planetColors[effectiveId] || planetColors[id],
                      }}
                    >
                      {/* ノースノード(☊)・サウスノード(☋)の記号を表示 */}
                      {id === "NorthNode"
                        ? "☊"
                        : id === "SouthNode"
                          ? "☋"
                          : PLANET_SYMBOLS[id] || PLANET_SYMBOLS[effectiveId]}
                    </span>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: "bold",
                          color: isDarkMode
                            ? "#ffffff"
                            : "rgb(136, 136, 136)",
                        }}
                      >
                        {id}
                      </span>
                      {isRetro(effectiveId) && (
                        <span
                          style={{
                            color: "#ef4444",
                            fontSize: "10px",
                            fontWeight: "900",
                          }}
                        >
                          Rx
                        </span>
                      )}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile
                        ? "22px 65px 1fr"
                        : "25px 85px 1fr",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "18px",
                        lineHeight: 1,
                        color: signColors[sign.symbol] || "#8b5cf6",
                        textAlign: "center",
                      }}
                    >
                      {sign.symbol + "\uFE0E"}
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: isDarkMode ? "#ffffff" : "rgb(136, 136, 136)",
                      }}
                    >
                      {sign.name}
                    </span>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "11px",
                          color: isDarkMode ? "#ffffff" : "#64748b",
                        }}
                      >
                        {formatDMS(lon)}
                      </span>
                      {dignity && (
                        <span
                          style={{
                            fontSize: "10px",
                            padding: "1px 4px",
                            borderRadius: "4px",
                            background:
                              dignity.score > 0 ? "#27ae60" : "#e74c3c",
                            color: "#ffffff",
                          }}
                        >
                          {dignity.label.split(" ")[0].toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: isDarkMode ? "#ffffff" : "#64748b",
                    }}
                  >
                    {declDisplay}
                  </div>

                  {/* ハウス列データ */}
                  <div
                    style={{
                      textAlign: "center",
                      fontSize: "13px",

                      color: isDarkMode ? "#ffffff" : "#1e293b",
                    }}
                  >
                    {houseNum}
                  </div>

                  <div style={{ textAlign: "center" }}>
                    <span
                      style={{
                        fontSize: "10px",
                        padding: isMobile ? "4px 6px" : "4px 10px",
                        borderRadius: "20px",
                        background: QUALITY_STYLE[sign.modality]
                          ? QUALITY_STYLE[sign.modality].bg
                          : "#f1f5f9",
                        color: "#fff",
                        display: "inline-block",
                        minWidth: isMobile ? "80px" : "100px",
                        fontWeight: "bold",
                      }}
                    >
                      {QUALITY_STYLE[sign.modality]
                        ? QUALITY_STYLE[sign.modality].label
                        : ""}
                    </span>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <span
                      style={{
                        fontSize: "10px",
                        padding: isMobile ? "4px 6px" : "4px 10px",
                        borderRadius: "20px",
                        background: ELEMENT_STYLE[sign.element]
                          ? ELEMENT_STYLE[sign.element].bg
                          : "#f1f5f9",
                        color: "#fff",
                        display: "inline-block",
                        minWidth: isMobile ? "65px" : "80px",
                        fontWeight: "bold",
                      }}
                    >
                      {ELEMENT_STYLE[sign.element]
                        ? ELEMENT_STYLE[sign.element].label
                        : ""}
                    </span>
                  </div>
                  <div style={{ color: "#94a3b8" }}>
                    {isOpen ? (
                      <ChevronUp size={18} />
                    ) : (
                      <ChevronDown size={18} />
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div
                    style={{
                      padding: "20px 30px",
                      background: isDarkMode
                        ? "rgba(255, 255, 255, 0.03)"
                        : "#f8fbff",
                      borderTop: "1px solid #f0f0f073",
                      display: "flex",
                      flexDirection: "column",
                      gap: "25px",
                    }}
                  >
                    {/* キーワードとサイン解説 */}
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "24px",
                            color:
                              planetColors[effectiveId] || planetColors[id],
                          }}
                        >
                          {id === "NorthNode"
                            ? "☊"
                            : id === "SouthNode"
                              ? "☋"
                              : PLANET_SYMBOLS[id] ||
                              PLANET_SYMBOLS[effectiveId]}
                        </span>
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "bold",
                            color: isDarkMode ? "#ffffff" : "#334155",
                          }}
                        >
                          {keywordsList.join(", ")}
                        </div>
                      </div>
                      <p
                        style={{
                          fontSize: "13px",
                          fontWeight: "bold",
                          lineHeight: "1.7",
                          color: isDarkMode
                            ? "rgba(255, 255, 255, 0.8)"
                            : "#475569",
                          margin: 0,
                          paddingLeft: "34px",
                          borderLeft: isDarkMode
                            ? "3px solid rgba(255, 255, 255, 0.1)"
                            : "3px solid #e2e8f0",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "18px",
                            marginRight: "8px",
                            verticalAlign: "middle",
                            color: signColors[sign.symbol] || "#8b5cf6",
                          }}
                        >
                          {sign.symbol + "\uFE0E"}
                        </span>
                        {signInterp}
                      </p>
                    </div>

                    {/* 逆行情報 */}
                    {!isHeliocentric && nextRetro && (
                      <div
                        style={{
                          padding: "12px 18px",
                          background: isDarkMode
                            ? "rgba(255, 255, 255, 0.05)"
                            : "#fff",
                          border: isDarkMode
                            ? "1px solid rgba(255, 255, 255, 0.1)"
                            : "1px solid #edf2f7",
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          gap: "15px",
                          boxShadow: isDarkMode
                            ? "none"
                            : "0 2px 4px rgba(0,0,0,0.02)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            color: isDarkMode ? "#ffffff" : "#64748b",
                          }}
                        >
                          <RefreshCw size={16} />
                          <span
                            style={{ fontSize: "12px", fontWeight: "bold" }}
                          >
                            次の逆行開始日
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: "bold",
                            color: isDarkMode ? "#ffffff" : "#64748b",
                          }}
                        >
                          {nextRetro.getFullYear()}年
                          {nextRetro.getMonth() + 1}月{nextRetro.getDate()}日
                          <span
                            style={{
                              marginLeft: "12px",
                              fontWeight: "bold",
                              color: isDarkMode ? "#ffffff" : "#64748b",
                            }}
                          >
                            {nextRetro.getHours().toString().padStart(2, "0")}
                            :
                            {nextRetro
                              .getMinutes()
                              .toString()
                              .padStart(2, "0")}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* クリティカル警告 */}
                    {planetCrit && (
                      <div
                        style={{
                          padding: "12px 18px",
                          background: isDarkMode
                            ? "rgba(255, 255, 255, 0.05)"
                            : "#fff",
                          border: isDarkMode
                            ? "1px solid rgba(255, 255, 255, 0.1)"
                            : "1px solid #edf2f7",
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          gap: "20px",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
                          color: isDarkMode ? "#ffffff" : "#64748b",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            color: "#e74c3c",
                          }}
                        >
                          <AlertTriangle size={18} />
                          <span
                            style={{
                              fontSize: "12px",
                              fontWeight: "bold",
                              color: isDarkMode ? "#ffffff" : "#64748b",
                            }}
                          >
                            クリティカルディグリー警告
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          {planetCrit.criticals.map(function (c, idx) {
                            return (
                              <div
                                key={idx}
                                style={{
                                  background:
                                    CRITICAL_TYPE_COLORS[c.type] || "#9b59b6",
                                  color: "#ffffff",
                                  padding: "4px 12px",
                                  borderRadius: "20px",
                                  fontSize: "11px",
                                  fontWeight: "bold",
                                }}
                              >
                                {CRITICAL_TYPE_NAMES[c.type]}:
                                {formatDegree(c.degree)}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* トランジット・アスペクトテーブル */}
                    <div
                      style={{
                        width: "100%",
                        borderTop: "1px solid #eeeeee7c",
                        paddingTop: "20px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "bold",
                          color: isDarkMode ? "#ffffff" : "#2c2d96",
                          marginBottom: "15px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Zap size={16} /> トランジット・アスペクト (T-T)
                      </div>
                      <div
                        style={{
                          background: isDarkMode ? "transparent" : "#fff",
                          border: isDarkMode
                            ? "1px solid rgba(255, 255, 255, 0.1)"
                            : "1px solid #e2e8f0",
                          borderRadius: "8px",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          className="preserved-grid"
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "1.2fr 1.3fr 1.2fr 0.7fr 1fr 0.7fr",
                            padding: "10px 16px",
                            background: isDarkMode
                              ? "rgba(255, 255, 255, 0.05)"
                              : "#f8fafc",
                            fontSize: "10px",
                            fontWeight: "bold",
                            color: isDarkMode ? "#ffffff" : "#94a3b8",
                            borderBottom: "1px solid #f0f0f06e",
                          }}
                        >
                          <div>FROM</div>
                          <div>アスペクト</div>
                          <div>TO</div>
                          <div style={{ textAlign: "right" }}>角度</div>
                          <div style={{ textAlign: "right" }}>ARC</div>
                          <div style={{ textAlign: "right" }}>Orb</div>
                        </div>

                        {planetAspects.length > 0 ? (
                          planetAspects.map(function (asp, i) {
                            var isMajAsp = Astro.ASPECTS.major.some(
                              function (m) {
                                return m.name === asp.name;
                              },
                            );
                            var aspLimit =
                              isMajAsp === true
                                ? aspectSettings.majorOrb
                                : aspectSettings.minorOrb;
                            var isBothOuterAsp =
                              OUTER_PLANETS_LIST.indexOf(effectiveId) !==
                              -1 &&
                              OUTER_PLANETS_LIST.indexOf(asp.p2) !== -1;
                            if (
                              aspectSettings.useOuterLimit === true &&
                              isBothOuterAsp === true
                            ) {
                              aspLimit = aspectSettings.outerPlanetOrb;
                            }
                            var isTight = asp.orb <= aspLimit * 0.1;

                            return (
                              <div
                                key={i}
                                className="preserved-grid"
                                style={{
                                  display: "grid",
                                  gridTemplateColumns:
                                    "1.2fr 1.3fr 1.2fr 0.7fr 1fr 0.7fr",
                                  padding: "10px 16px",
                                  background: isDarkMode
                                    ? isTight
                                      ? "rgba(255, 255, 255, 0.08)"
                                      : "transparent"
                                    : isTight
                                      ? "#f9f9f5"
                                      : "#fff",
                                  alignItems: "center",
                                  fontSize: "12px",
                                  color: isDarkMode ? "#ffffff" : "#64748b",
                                  borderBottom: "1px solid #f0f0f071",
                                }}
                              >
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns:
                                      "15px 28px 70px 20px",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "9px",
                                      color: "#cbd5e1",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    T
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "24px",
                                      color:
                                        planetColors[effectiveId] ||
                                        planetColors[id],
                                    }}
                                  >
                                    {id === "NorthNode"
                                      ? "☊"
                                      : id === "SouthNode"
                                        ? "☋"
                                        : PLANET_SYMBOLS[id] ||
                                        PLANET_SYMBOLS[effectiveId]}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "13px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {id}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "14px",
                                      color:
                                        signColors[sign.symbol] || "#8b5cf6",
                                      textAlign: "center",
                                    }}
                                  >
                                    {sign.symbol + "\uFE0E"}
                                  </span>
                                </div>

                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: "20px",
                                        color:
                                          asp.type === "Hard"
                                            ? "#e74c3c"
                                            : "#3498db",
                                      }}
                                    >
                                      {asp.symbol}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      {asp.name}
                                      {asp.name === "Square" && (
                                        <span
                                          style={{
                                            fontWeight: "normal",
                                            fontSize: "12px",
                                            marginLeft: "2px",
                                          }}
                                        >
                                          {getSquareLabel(
                                            effectiveId,
                                            lon,
                                            asp.p2,
                                            transitPositions[asp.p2],
                                          )}
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                  <span
                                    style={{
                                      fontSize: "10px",
                                      color:
                                        asp.type === "Hard"
                                          ? "#e74c3c"
                                          : "#3498db",
                                    }}
                                  >
                                    {asp.type === "Hard"
                                      ? "▼BEARISH"
                                      : "▲BULLISH"}
                                  </span>
                                </div>

                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns:
                                      "15px 28px 70px 20px",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "9px",
                                      color: "#cbd5e1",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    T
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "24px",
                                      color: planetColors[asp.p2],
                                    }}
                                  >
                                    {PLANET_SYMBOLS[asp.p2]}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "13px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {asp.p2}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "14px",
                                      color:
                                        signColors[asp.sign2.symbol] ||
                                        "#8b5cf6",
                                      textAlign: "center",
                                    }}
                                  >
                                    {asp.sign2.symbol + "\uFE0E"}
                                  </span>
                                </div>

                                <div
                                  style={{
                                    textAlign: "right",
                                    fontSize: "11px",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {asp.angle}°
                                </div>
                                <div
                                  style={{
                                    textAlign: "right",
                                    fontSize: "11px",
                                  }}
                                >
                                  {formatDMS(asp.dist)}
                                </div>
                                <div
                                  style={{
                                    textAlign: "right",
                                    fontSize: "11px",
                                    fontWeight: isTight ? "bold" : "normal",
                                  }}
                                >
                                  {asp.orb.toFixed(2)}°
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div
                            style={{
                              padding: "20px",
                              textAlign: "center",
                              color: isDarkMode ? "#ffffff" : "#64748b",
                              fontSize: "12px",
                            }}
                          >
                            主要アスペクトなし
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}